import React, { useEffect, useState } from 'react';
import axiosInstance from '../../AxiosInstance';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faChevronDown, faChevronUp, faUser, faClock, faImage, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import RecordFormModal from './components/record_form_modal';
import Modal from '../../components/modal/modal';
import {useTranslation} from "react-i18next";
import PaginationControls, {getStoredPageSize} from "../../components/pagination/PaginationControls";

function ExpandedRecord({ record }) {
    const {t} = useTranslation();
    return (
        <div style={{ padding: '16px 20px', background: '#F8FAFC', borderTop: '1px solid #E2E8F0' }}>
            {record.notes && (
                <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '12px', color: '#94A3B8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('maintenance.records.notes')}</div>
                    <div style={{ fontSize: '14px', color: '#334155', whiteSpace: 'pre-wrap' }}>{record.notes}</div>
                </div>
            )}

            {record.photos?.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '12px', color: '#94A3B8', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        <FontAwesomeIcon icon={faImage} /> {t('maintenance.records.photos', {count: record.photos.length})}
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                        {record.photos.map(p => (
                            <a key={p.id} href={p.image} target="_blank" rel="noopener noreferrer">
                                <img src={p.image} alt={p.caption || 'photo'} style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #E2E8F0' }} />
                            </a>
                        ))}
                    </div>
                </div>
            )}

            {record.parts_used?.length > 0 && (
                <div>
                    <div style={{ fontSize: '12px', color: '#94A3B8', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('maintenance.records.partsUsed')}</div>
                    <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                        <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#F1F5F9', borderBottom: '1px solid #E2E8F0' }}>
                                    <th style={{ padding: '8px 12px', textAlign: 'left', color: '#475569', fontWeight: 600, whiteSpace: 'nowrap' }}>{t('maintenance.records.part')}</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'left', color: '#475569', fontWeight: 600, whiteSpace: 'nowrap' }}>{t('maintenance.records.brand')}</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'left', color: '#475569', fontWeight: 600, whiteSpace: 'nowrap' }}>{t('maintenance.records.pn')}</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'right', color: '#475569', fontWeight: 600, whiteSpace: 'nowrap' }}>{t('maintenance.records.qty')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {record.parts_used.map((p, i) => (
                                    <tr key={p.id} style={{ borderTop: i > 0 ? '1px solid #F1F5F9' : 'none', background: i % 2 === 0 ? '#FFF' : '#FAFAFA' }}>
                                        <td style={{ padding: '8px 12px', color: '#1E293B', fontWeight: 500 }}>{p.name}</td>
                                        <td style={{ padding: '8px 12px', color: '#64748B' }}>{p.brand || '—'}</td>
                                        <td style={{ padding: '8px 12px', color: '#64748B', fontFamily: 'monospace', fontSize: '12px' }}>{p.part_number || '—'}</td>
                                        <td style={{ padding: '8px 12px', textAlign: 'right', color: '#4F46E5', fontWeight: 700 }}>{p.quantity}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function MaintenanceRecordsTab({ machine }) {
    const {t} = useTranslation();
    const [records, setRecords] = useState([]);
    const [technicianFilter, setTechnicianFilter] = useState('');
    const [technicians, setTechnicians] = useState([]);
    const [expandedId, setExpandedId] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(getStoredPageSize);
    const [showModal, setShowModal] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const fetchRecords = () => {
        const params = `machine_id=${machine.id}${technicianFilter ? '&performed_by_id=' + technicianFilter : ''}`;
        axiosInstance.get(`maintenance_record/?${params}`).then(res => {
            setRecords(res.data);
            setPage(1);
            const seen = {};
            const techs = [];
            res.data.forEach(r => {
                if (r.performed_by && !seen[r.performed_by]) {
                    seen[r.performed_by] = true;
                    techs.push({ id: r.performed_by, name: r.performed_by_full_name || r.performed_by_username || 'Technician' });
                }
            });
            setTechnicians(techs);
        });
    };

    useEffect(() => {
        fetchRecords();
    }, [machine.id, technicianFilter]);

    const confirmDelete = () => {
        axiosInstance.delete(`maintenance_record/${deleteTarget.id}/`).then(() => {
            setDeleteTarget(null);
            fetchRecords();
        });
    };

    const openEdit = (e, record) => {
        e.stopPropagation();
        setEditRecord(record);
        setShowModal(true);
    };

    const openDelete = (e, record) => {
        e.stopPropagation();
        setDeleteTarget(record);
    };

    const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div className="d-flex gap-2 align-items-center">
                    <select
                        value={technicianFilter}
                        onChange={e => setTechnicianFilter(e.target.value)}
                        style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '14px', color: '#334155', background: '#FFF' }}
                    >
                        <option value="">{t('maintenance.records.allTechnicians')}</option>
                        {technicians.map(tech => <option key={tech.id} value={tech.id}>{tech.name}</option>)}
                    </select>
                </div>
                <button
                    onClick={() => { setEditRecord(null); setShowModal(true); }}
                    style={{
                        padding: '8px 16px', borderRadius: '8px', border: 'none',
                        background: '#4F46E5', color: '#FFF', fontWeight: 600,
                        fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
                    }}
                >
                    <FontAwesomeIcon icon={faPlus} /> {t('maintenance.createRecord')}
                </button>
            </div>

            {records.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px', color: '#94A3B8' }}>
                    {t('maintenance.records.noRecords')}
                </div>
            ) : (() => {
                    const totalPages = Math.ceil(records.length / pageSize);
                    const pagedRecords = records.slice((page - 1) * pageSize, page * pageSize);
                    const handlePageChange = (newPage) => { setPage(newPage); setExpandedId(null); };
                    const handlePageSizeChange = (newSize) => { setPageSize(newSize); setPage(1); setExpandedId(null); };
                    return (
                <div style={{ background: '#FFF', borderRadius: '12px', border: '1px solid #E2E8F0', overflow: 'hidden', paddingBottom: '12px' }}>
                    {pagedRecords.map((record, idx) => (
                        <div key={record.id} style={{ borderTop: idx > 0 ? '1px solid #E2E8F0' : 'none' }}>
                            <div
                                className="d-flex justify-content-between align-items-center flex-wrap gap-2"
                                style={{ padding: '14px 20px', cursor: 'pointer' }}
                                onClick={() => setExpandedId(expandedId === record.id ? null : record.id)}
                            >
                                <div className="d-flex flex-column gap-1">
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <span style={{ fontWeight: 600, color: '#1E293B', fontSize: '14px' }}>
                                            {record.plan_name || t('maintenance.records.adHoc')}
                                        </span>
                                        {record.photos?.length > 0 && (
                                            <span style={{ fontSize: '12px', color: '#94A3B8' }}>
                                                <FontAwesomeIcon icon={faImage} /> {record.photos.length}
                                            </span>
                                        )}
                                        {record.parts_used?.length > 0 && (
                                            <span style={{ fontSize: '12px', padding: '1px 6px', borderRadius: '4px', background: '#F5F3FF', color: '#7C3AED' }}>
                                                {t('maintenance.records.partsCount', {count: record.parts_used.length})}
                                            </span>
                                        )}
                                    </div>
                                    <div className="d-flex gap-3 flex-wrap" style={{ fontSize: '12px', color: '#94A3B8' }}>
                                        <span><FontAwesomeIcon icon={faClock} /> {formatDate(record.performed_at)}</span>
                                        {record.performed_by_full_name && (
                                            <span><FontAwesomeIcon icon={faUser} /> {record.performed_by_full_name}</span>
                                        )}
                                        {record.hours_at_maintenance != null && (
                                            <span>{t('maintenance.records.hoursAtTime', {hours: record.hours_at_maintenance.toFixed(1)})}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <button
                                        onClick={e => openEdit(e, record)}
                                        style={{ background: 'none', border: 'none', color: '#64748B', cursor: 'pointer', padding: '6px' }}
                                        title={t('common.edit')}
                                    >
                                        <FontAwesomeIcon icon={faEdit} />
                                    </button>
                                    <button
                                        onClick={e => openDelete(e, record)}
                                        style={{ background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', padding: '6px' }}
                                        title={t('common.delete')}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                    <FontAwesomeIcon icon={expandedId === record.id ? faChevronUp : faChevronDown} style={{ color: '#94A3B8' }} />
                                </div>
                            </div>
                            {expandedId === record.id && <ExpandedRecord record={record} />}
                        </div>
                    ))}
                    <div style={{ marginTop: '10px', marginLeft: '6px' }}>
                        <PaginationControls
                            page={page}
                            totalPages={totalPages}
                            pageSize={pageSize}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                            totalCount={records.length}
                        />
                    </div>
                </div>
                    );
                })()}

            {showModal && (
                <RecordFormModal
                    show={showModal}
                    setter={setShowModal}
                    machine={machine}
                    plan={null}
                    record={editRecord}
                    onSaved={() => { setShowModal(false); setEditRecord(null); fetchRecords(); }}
                />
            )}

            <Modal
                show={!!deleteTarget}
                setter={() => setDeleteTarget(null)}
                name={t('maintenance.deleteRecord')}
                buttonName={t('common.delete')}
                onClick={confirmDelete}
                danger
            >
                <p>{t('maintenance.records.confirmDelete', { date: formatDate(deleteTarget?.performed_at) })}</p>
            </Modal>
        </div>
    );
}
